/* eslint-disable react/no-array-index-key */
import React, {
  lazy,
  Suspense,
  Fragment
} from 'react';
import {
  Switch,
  Redirect,
  Route,
  useLocation
} from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import AdminLayout from 'src/layouts/DashboardLayout';
import HomeLayout from 'src/layouts/HomeLayout';
import InventoryRouteLayout from 'src/layouts/InventoryRouteLayout';
import SiteFooter from 'src/components/SiteFooter';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import UserGuard from 'src/components/UserGuard';
import OnboardingRoute from 'src/components/OnboardingRoute';
import { isOfferFlowLightTheme, isOnboardingPageLightTheme } from 'src/views/listView/inventoryUiFlags';
import { ONBOARDING_PATH } from 'src/utils/onboardingRedirect';
import { getOnboardingThemeTokens } from 'src/views/onBoardingPage/onboardingTheme';

/** Inventory offer-flow routes that share light/dark page chrome with request quote. */
const INVENTORY_OFFER_FLOW_PATH = /^\/app\/solar-panels\/(get-spec|make-offer|make-offer-inverter|request-quote|request-quote-inverter)(\/|$)/;

function getShellContentBg(pathname, source) {
  if (pathname === ONBOARDING_PATH) {
    return getOnboardingThemeTokens(isOnboardingPageLightTheme()).pageBg || '#0c111a';
  }
  const isLightTheme = INVENTORY_OFFER_FLOW_PATH.test(pathname)
    ? isOfferFlowLightTheme(source)
    : source === 'productPage';
  return isLightTheme ? '#f8f8ff' : '#0c111a';
}

/**
 * No-layout shell: light fill for inventory offer flows (while dark theme is disabled),
 * product-detail entry, and onboarding (while its dark theme is disabled); otherwise dark.
 */
function NoLayoutPageShell({ children, hideFooter = false }) {
  const location = useLocation();
  const source = location.state?.source;

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0c111a',
      }}
    >
      <div
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: getShellContentBg(location.pathname, source),
        }}
      >
        {children}
      </div>
      {!hideFooter && <SiteFooter />}
    </div>
  );
}

const renderRoutes = (routes) => (routes ? (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                {route.routes || route.layout ? (
                  <Layout>
                    {route.routes
                      ? renderRoutes(route.routes)
                      : <Component {...props} />}
                  </Layout>
                ) : (
                  <NoLayoutPageShell hideFooter={route.hideFooter}>
                    <Layout>
                      <Component {...props} />
                    </Layout>
                  </NoLayoutPageShell>
                )}
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
) : null);

function Routes() {
  const routesConfig = [
    // {
    //   exact: true,
    //   path: '/',
    //   component: () => <Redirect to="/wholesale-solar-panels" />
    //   // component: () => <Redirect to="/404" />
    // },
    {
      exact: true,
      guard: GuestGuard,
      layout: HomeLayout,
      path: '/login',
      component: lazy(() => import('src/views/auth/LoginView'))
    },
    {
      exact: true,
      guard: GuestGuard,
      layout: HomeLayout,
      path: '/register',
      component: lazy(() => import('src/views/auth/RegisterView'))
    },
    {
      exact: true,
      guard: GuestGuard,
      layout: HomeLayout,
      path: '/guest/login',
      component: lazy(() => import('src/views/auth/GuestLoginView'))
    },
    // {
    //   exact: true,
    //   guard: GuestGuard,
    //   layout: HomeLayout,
    //   path: '/home',
    //   component: lazy(() => import('src/views/auth/LoginView'))
    // },
    // {
    //   exact: true,
    //   guard: GuestGuard,
    //   layout: HomeLayout,
    //   path: '/procurement-services',
    //   component: lazy(() => import('src/views/auth/LoginView'))
    // },
    // {
    //   exact: true,
    //   guard: GuestGuard,
    //   layout: HomeLayout,
    //   path: '/o-m-procurement',
    //   component: lazy(() => import('src/views/auth/LoginView'))
    // },
    {
      exact: true,
      guard: GuestGuard,
      layout: HomeLayout,
      path: '/review',
      component: lazy(() => import('src/views/CustomerReview'))
    },
    {
      exact: true,
      guard: GuestGuard,
      path: '/admin/login',
      component: lazy(() => import('src/views/auth/AdminLoginView'))
    },
    {
      exact: true,
      path: '/404',
      component: lazy(() => import('src/views/pages/Error404View'))
    },
    {
      exact: true,
      path: '/blankpage',
      component: lazy(() => import('src/views/pages/BlankPage'))
    },
    {
      exact: true,
      // Stable layout: guest/logged-in chrome swaps inside InventoryRouteLayout
      // so unlock-modal login does not remount the inventory page tree.
      layout: InventoryRouteLayout,
      path: '/app/solar-panels',
      component: lazy(() => import('src/views/listView'))
    },
    {
      exact: true,
      path: '/app/solar-panels/get-spec/:id',
      component: lazy(() => import('src/views/getSpecs'))
    },
    {
      exact: true,
      path: '/app/solar-panels/make-offer/:id',
      component: lazy(() => import('src/views/makeOffer'))
    },
    {
      exact: true,
      path: '/app/solar-panels/make-offer-inverter/:id',
      component: lazy(() => import('src/views/makeOfferInverter'))
    },
    {
      exact: true,
      path: '/app/solar-panels/request-quote/:id',
      component: lazy(() => import('src/views/requestQuote'))
    },
    {
      exact: true,
      path: '/app/solar-panels/request-quote-inverter/:id',
      component: lazy(() => import('src/views/requestQuoteInverters'))
    },
    {
      exact: true,
      path: '/app/solar-panels/request-product',
      component: lazy(() => import('src/views/requestProduct'))
    },
    {
      exact: true,
      path: '/app/solar-panels/product-offer',
      component: lazy(() => import('src/views/ProductOffer'))
    },
    {
      exact: true,
      path: '/app/solar-panels/news',
      component: lazy(() => import('src/views/News'))
    },
    {
      exact: true,
      path: '/app/solar-panels/events',
      // Footer renders inside the page's scroll area (keeps the mobile browser bar open).
      hideFooter: true,
      component: lazy(() => import('src/views/Events'))
    },
    {
      exact: true,
      path: '/app/solar-panels/events/:id',
      // Footer renders inside the page's scroll area (keeps the mobile browser bar open).
      hideFooter: true,
      component: lazy(() => import('src/views/EventDetails'))
    },
    {
      exact: true,
      path: "/app/solar-panels/:brand/:type/:model/:wattage/:id",
      hideFooter: true,
      component: lazy(() => import('src/views/ProductDetails'))
    },
    {
      exact: true,
      path: "/app/solar-panels/:brand/:type/:model/:model1/:wattage/:id",
      hideFooter: true,
      component: lazy(() => import('src/views/ProductDetails'))
    },
    {
      exact: true,
      path: "/app/solar-panels/:brand/:type/:model/:model1/:model2/:wattage/:id",
      hideFooter: true,
      component: lazy(() => import('src/views/ProductDetails'))
    },
    {
      path: '/app',
      guard: AuthGuard,
      routes: [
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/interface/home',
          component: lazy(() => import('src/views/Admin/AdminBoard'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/allproducts/list',
          component: lazy(() => import('src/views/Admin/AllProducts'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/edit-product/:id',
          component: lazy(() => import('src/views/Admin/EditProduct'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/contact/list',
          component: lazy(() => import('src/views/Admin/EditContact'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/saveSearch/results',
          component: lazy(() => import('src/views/Admin/SearchResults'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/hiddenData/list',
          component: lazy(() => import('src/views/Admin/HiddenData'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/server/control',
          component: lazy(() => import('src/views/Admin/ServerControl'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/news/management',
          component: lazy(() => import('src/views/Admin/NewsManagement'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/customers/list',
          component: lazy(() => import('src/views/Admin/Customers'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/hubspot/controls',
          component: lazy(() => import('src/views/Admin/HubSpotControls'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/hubspot/tags',
          component: lazy(() => import('src/views/Admin/TagManagement'))
        },
        {
          exact: true,
          guard: UserGuard,
          layout: AdminLayout,
          path: '/app/admin/hubspot/product-changes',
          component: lazy(() => import('src/views/Admin/ProductChangeApproval'))
        },
        {
          exact: true,
          path: '/app',
          component: () => <Redirect to="/app/solar-panels?terms=modules" />
        },
        {
          exact: true,
          path: '/app/solar-panels/onboarding',
          guard: OnboardingRoute,
          component: lazy(() => import('src/views/onBoardingPage'))
        },
        {
          exact: true,
          path: '/app/solar-panels/favorites',
          layout: AdminLayout,
          component: lazy(() => import('src/views/favoritedProducts'))
        },
        {
          exact: true,
          path: '/app/solar-panels/other-alerts',
          layout: AdminLayout,
          component: lazy(() => import('src/views/otherAlerts'))
        },
        {
          exact: true,
          path: "/app/solar-panels/inverters/:brand/:type/:model/:id",
          hideFooter: true,
          component: lazy(() => import('src/views/ProductDetails'))
        },


        {
          component: () => <Redirect to="/404" />
        }
      ]
    },
    {
      path: '*',
      routes: [
        {
          component: () => <Redirect to="/404" />
        }
      ]
    }
  ];

  return renderRoutes(routesConfig);
}

export default Routes;
